import { Link } from 'react-router-dom';
import { ChevronRight, Receipt, Store } from 'lucide-react';
import { isTerminalOrder } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { formatCOP, formatFecha } from '@/lib/cn';
import { useMisPedidos, type Pedido } from '@/lib/queries';
import { Boton, Cargando, ErrorCarga, Vacio } from '@/components/ui';
import { EtiquetaEstado } from '@/components/EtiquetaEstado';

export function MisPedidos() {
  const pedidos = useMisPedidos();

  if (pedidos.isLoading) return <Cargando texto="Cargando tus pedidos…" />;
  if (pedidos.isError) {
    return (
      <div className="mx-auto max-w-2xl py-8">
        <ErrorCarga mensaje={getErrorMessage(pedidos.error)} onReintentar={() => pedidos.refetch()} />
      </div>
    );
  }

  const lista = pedidos.data?.data ?? [];
  const activos = lista.filter((p) => !isTerminalOrder(p.status));
  const anteriores = lista.filter((p) => isTerminalOrder(p.status));

  return (
    <div className="mx-auto max-w-2xl pb-8">
      <h1 className="text-3xl text-kick-dark">Mis pedidos</h1>

      {lista.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<Receipt className="h-6 w-6" />}
            titulo="Todavía no has pedido nada"
            descripcion="Cuando hagas tu primer pedido lo verás aquí, con su estado en vivo."
            accion={
              <Link to="/">
                <Boton>Ver comercios</Boton>
              </Link>
            }
          />
        </div>
      ) : (
        <>
          {activos.length > 0 && (
            <section className="mt-6">
              <h2 className="text-lg text-kick-dark">En curso</h2>
              <ul className="mt-3 space-y-3">
                {activos.map((pedido) => (
                  <Tarjeta key={pedido.id} pedido={pedido} />
                ))}
              </ul>
            </section>
          )}

          {anteriores.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg text-kick-dark">Anteriores</h2>
              <ul className="mt-3 space-y-3">
                {anteriores.map((pedido) => (
                  <Tarjeta key={pedido.id} pedido={pedido} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Tarjeta({ pedido }: { pedido: Pedido }) {
  return (
    <li>
      <Link
        to={`/pedidos/${pedido.id}`}
        className="card-kick flex items-center gap-3 p-4 transition hover:shadow-md"
      >
        {pedido.merchantLogo ? (
          <img
            src={pedido.merchantLogo}
            alt=""
            className="h-12 w-12 shrink-0 rounded-kick object-cover"
          />
        ) : (
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray/50">
            <Store className="h-5 w-5" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-kick-ink">{pedido.merchantName}</p>
          <p className="text-sm text-kick-gray">
            {pedido.code} · {formatFecha(pedido.createdAt)}
          </p>
          <EtiquetaEstado estado={pedido.status} className="mt-1.5" />
        </div>

        <div className="text-right">
          <p className="font-bold text-kick-dark">{formatCOP(pedido.total)}</p>
          <ChevronRight className="ml-auto mt-1 h-4 w-4 text-kick-gray" />
        </div>
      </Link>
    </li>
  );
}

